import React, { useState, useEffect, useRef } from 'react';

// SVG Icons for social media
const TwitterIcon = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>;
const FacebookIcon = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3v9h4z"></path></svg>;
const WhatsAppIcon = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M16.75 13.96c.25.58.93.83 1.25 1.25.12.17.25.33.25.5 0 .25-.25.5-.25.5s-1.08 1.17-2.33 1.17c-1.5 0-2.83-1.08-4.17-2.25-1.75-1.58-2.83-3.42-2.92-3.58s-.08-.25-.08-.33c0-.08.08-.17.17-.25.17-.08.33-.25.5-.42.25-.17.42-.25.58-.42.25-.17.42-.25.5-.25s.42 0 .58.25c.17.25.5.58.58.67.17.17.25.25.33.42.08.17.08.25.08.42s-.08.33-.17.5c-.08.17-.17.25-.25.33-.17.17-.25.25-.42.42-.17.17-.25.25-.42.42-.17.17-.25.25-.42s.17.42.5.92c.5.67 1.17 1.25 1.92 1.67.67.33 1.08.42 1.25.42.25 0 .67-.25.92-.5.25-.25.42-.5.5-.67.08-.17.17-.17.33-.08.17.08.92.42 1.08.5.17.08.25.17.33.25.08.17.08.25 0 .5zM12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path></svg>;
const LinkedInIcon = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"></path></svg>;
const MailIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z"></path></svg>;
const LinkIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17 7h-4v2h4c1.65 0 3 1.35 3 3s-1.35 3-3 3h-4v2h4c2.76 0 5-2.24 5-5s-2.24-5-5-5zm-6 8H7c-1.65 0-3-1.35-3-3s1.35-3 3-3h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-2zM8 11h8v2H8z"></path></svg>;

interface ShareButtonProps {
  title?: string;
  text?: string;
  className?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({ title = document.title, text = "Check out this useful resource from ChargesDecoder!", className = '' }) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [feedback, setFeedback] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);
  
    // Close popover on outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setPopoverVisible(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);
    
    const getShareableUrl = () => {
        const canonicalLink = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
        let baseUrl = (canonicalLink ? canonicalLink.href : window.location.origin).replace(/\/$/, '') + '/';
        
        // Ensure there's always a hash for the HashRouter, defaulting to the root.
        const hash = window.location.hash || '#/';
        const url = new URL(hash, baseUrl).href;
        return url;
    };
  
    const handleShareClick = async () => {
        const urlToShare = getShareableUrl();
    
        if (navigator.share) {
            try {
                await navigator.share({ title, text, url: urlToShare });
            } catch (error) {
                console.error('Error sharing:', error);
            }
        } else {
            setPopoverVisible(prev => !prev);
        }
    };
    
    const handleCopyLink = async () => {
        const urlToCopy = getShareableUrl();
        try {
            await navigator.clipboard.writeText(urlToCopy);
            setFeedback('Copied!');
        } catch (error) {
            console.error('Error copying URL:', error);
            setFeedback('Copy failed.');
        }
        setTimeout(() => {
            setFeedback('');
            setPopoverVisible(false);
        }, 1500);
    };
    
    const encodedUrl = encodeURIComponent(getShareableUrl());
    const encodedTitle = encodeURIComponent(title);
    const encodedText = encodeURIComponent(text);

    const socialLinks = [
        { name: 'Twitter', icon: <TwitterIcon />, href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}` },
        { name: 'Facebook', icon: <FacebookIcon />, href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
        { name: 'WhatsApp', icon: <WhatsAppIcon />, href: `https://api.whatsapp.com/send?text=${encodedText} ${encodedUrl}` },
        { name: 'LinkedIn', icon: <LinkedInIcon />, href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${encodedText}` },
        { name: 'Email', icon: <MailIcon />, href: `mailto:?subject=${encodedTitle}&body=${encodedText} ${encodedUrl}` },
    ];

    return (
        <div ref={wrapperRef} className={`relative inline-block ${className}`}>
            <button
                onClick={handleShareClick}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition"
                aria-label="Share this page"
                aria-haspopup="true"
                aria-expanded={popoverVisible}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                <span>Share</span>
            </button>

            {popoverVisible && (
                <div 
                    className="absolute z-20 right-0 mt-2 w-56 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none animate-fade-in-down"
                    role="menu" aria-orientation="vertical" aria-labelledby="menu-button"
                >
                    <div className="py-1" role="none">
                        <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Share via</p>
                        {socialLinks.map(link => (
                            <a
                                key={link.name}
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-700 flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100"
                                role="menuitem"
                            >
                                {link.icon}
                                <span>{link.name}</span>
                            </a>
                        ))}
                        <div className="border-t border-gray-100 my-1"></div>
                        <button
                            onClick={handleCopyLink}
                            className="w-full text-left text-gray-700 flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100"
                            role="menuitem"
                        >
                            <LinkIcon />
                            <span>{feedback || 'Copy Link'}</span>
                        </button>
                    </div>
                </div>
            )}
            <style>{`
                @keyframes fade-in-down {
                    0% {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-down {
                    animation: fade-in-down 0.2s ease-out;
                }
            `}</style>
        </div>
    );
};

export default ShareButton;